import { useCallback, useSyncExternalStore } from 'react';

/** `local` survives a browser restart. `session` lasts as long as the tab. */
export type StorageArea = 'local' | 'session';

export interface UseStoredValueOptions<T> {
  key: string;
  /**
   * Narrows the raw stored string, which is `null` when nothing is stored. Return the fallback
   * for anything unexpected: storage is user-writable and outlives the code that wrote it, so a
   * value from three releases ago can still turn up here.
   */
  fromStored: (stored: string | null) => T;
  /** @default 'local' */
  area?: StorageArea;
  /**
   * Serialises the value on the way in. The default suits the string unions this mostly holds.
   * Pass `JSON.stringify` for anything richer, and parse it back in `fromStored`. Note that
   * `fromStored` runs on every render, so a parsed object is a fresh reference each time: memoise
   * it in the consumer before it reaches a dependency array or a memoised child.
   *
   * @default String
   */
  toStored?: (value: T) => string;
}

// Listeners are scoped to the key and area they read, so a write only wakes the hooks that care.
// Another tab's write arrives as a `storage` event instead, filtered to the same key on the way in.
const listeners = new Map<string, Set<() => void>>();

const listenerKey = (key: string, area: StorageArea) => `${area}:${key}`;

const storageFor = (area: StorageArea) => (area === 'session' ? sessionStorage : localStorage);

const readStored = (key: string, area: StorageArea) => {
  try {
    return storageFor(area).getItem(key);
  } catch {
    return null;
  }
};

const writeStored = (key: string, area: StorageArea, value: string) => {
  try {
    storageFor(area).setItem(key, value);
  } catch {
    // Storage can be over quota or blocked outright. Nothing is cached in memory, so the value
    // reverts to the fallback on the next read rather than half-applying.
  }
  for (const notify of listeners.get(listenerKey(key, area)) ?? []) {
    notify();
  }
};

const subscribeTo = (key: string, area: StorageArea, listener: () => void) => {
  const id = listenerKey(key, area);
  const forKey = listeners.get(id) ?? new Set();
  forKey.add(listener);
  listeners.set(id, forKey);
  // Another tab's write. `key` is null when it cleared the whole area, which counts as ours going.
  const onStorageEvent = (event: StorageEvent) => {
    if (event.key === null || event.key === key) {
      listener();
    }
  };
  window.addEventListener('storage', onStorageEvent);
  return () => {
    forKey.delete(listener);
    // Drop the empty set rather than leaving a key behind for every preference ever read. The
    // identity check keeps a late cleanup from taking out a set a newer subscriber has since put
    // in its place.
    if (forKey.size === 0 && listeners.get(id) === forKey) {
      listeners.delete(id);
    }
    window.removeEventListener('storage', onStorageEvent);
  };
};

/**
 * A value backed by Web Storage, in the shape of `useState`.
 *
 * Storage is the single source of truth, and every hook reading a key re-renders when any of
 * them writes it. That is the point: two components showing the same preference stay in step
 * instead of drifting until the next mount. Writes from other tabs count too, so a `local`
 * preference stays in step across the whole browser.
 *
 * Give the preference its own named hook rather than calling this from a component, so the key,
 * the fallback and the narrowing live in one place:
 *
 * @example
 *
 * ```ts
 * export const useAttachmentLayout = (fallback: FileUploadLayout) =>
 *   useStoredValue({
 *     key: 'attachment-layout',
 *     fromStored: (stored) => (isAttachmentLayout(stored) ? stored : fallback),
 *   });
 * ```
 */
export const useStoredValue = <T>({ key, fromStored, area = 'local', toStored = String }: UseStoredValueOptions<T>) => {
  const subscribe = useCallback((listener: () => void) => subscribeTo(key, area, listener), [key, area]);
  const getSnapshot = useCallback(() => readStored(key, area), [key, area]);
  const stored = useSyncExternalStore(subscribe, getSnapshot);

  const setValue = useCallback((value: T) => writeStored(key, area, toStored(value)), [key, area, toStored]);

  return [fromStored(stored), setValue] as const;
};
